import { useCallback, useContext, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { FiActivity, FiPause, FiPlay, FiTrash2, FiX } from "react-icons/fi";
import { AppState } from "../../lib/context/AppProvider";
import {
    MAX_ENTRIES, clearEntries, getEntries, installNetworkMonitor, setPaused, setRecording, subscribe,
} from "../../lib/network/networkMonitor";
import NetworkRequestDetail from "./NetworkRequestDetail";
import { formatBytes, formatClock, formatDuration, shortContentType, splitUrl, statusClass, statusLabel } from "./netdiagUtils";
import "../../styles/NetworkDiagnostics.css";

const MIN_HEIGHT = 220;
const DEFAULT_HEIGHT = 400;
const HEIGHT_KEY = "netdiagHeight";
// Keep the slide in step with the CSS transition so the body stays mounted while it plays.
const SLIDE_MS = 240;

const readHeight = () => {
    const stored = Number.parseInt(globalThis.localStorage?.getItem?.(HEIGHT_KEY) ?? "", 10);
    return Number.isFinite(stored) && stored >= MIN_HEIGHT ? stored : DEFAULT_HEIGHT;
};

const isFailure = (entry) => !!entry.error || (entry.status ?? 0) >= 400;

// A slide-up drawer listing every request the app makes, with headers, status and payloads.
// Enabled per user via Settings → General → "Network diagnostics panel".
const NetworkDiagnostics = () => {
    const { userDetails } = useContext(AppState);
    const enabled = userDetails?.Settings?.networkDiagnostics === "Yes";

    const entries = useSyncExternalStore(subscribe, getEntries);

    const [open, setOpen] = useState(false);
    // `mounted` keeps the drawer in the DOM for the closing slide; `shown` drives the transform.
    const [mounted, setMounted] = useState(false);
    const [shown, setShown] = useState(false);
    const [pausedState, setPausedState] = useState(false);
    const [filter, setFilter] = useState("");
    const [errorsOnly, setErrorsOnly] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    const [height, setHeight] = useState(readHeight);

    const listRef = useRef(null);
    // Follow new requests only while the list is already scrolled to the bottom.
    const stickToBottom = useRef(true);

    useEffect(() => {
        installNetworkMonitor();
    }, []);

    // The setting is the single source of truth for capture; turning it off drops the buffer so
    // an old session's traffic isn't sitting there the next time it's switched on.
    //
    // Nothing is synced until user-details have loaded: until then the persisted flag from the
    // last session is what's recording, which is how login and the first page of requests get
    // captured at all.
    useEffect(() => {
        if (!userDetails) return;
        setRecording(enabled);
        if (!enabled) {
            clearEntries();
            setOpen(false);
        }
    }, [userDetails, enabled]);

    useEffect(() => {
        if (open) {
            setMounted(true);
            const raf = requestAnimationFrame(() => setShown(true));
            return () => cancelAnimationFrame(raf);
        }
        setShown(false);
        const timer = setTimeout(() => setMounted(false), SLIDE_MS);
        return () => clearTimeout(timer);
    }, [open]);

    useEffect(() => {
        if (!open) return;
        const onKeyDown = (e) => {
            if (e.key === "Escape") setOpen(false);
        };
        document.addEventListener("keydown", onKeyDown);
        return () => document.removeEventListener("keydown", onKeyDown);
    }, [open]);

    const filtered = useMemo(() => {
        const term = filter.trim().toLowerCase();
        return entries.filter((entry) => {
            if (errorsOnly && !isFailure(entry)) return false;
            if (!term) return true;
            return entry.url.toLowerCase().includes(term)
                || entry.method.toLowerCase().includes(term)
                || String(entry.status ?? "").includes(term);
        });
    }, [entries, filter, errorsOnly]);

    const selected = useMemo(() => entries.find((entry) => entry.id === selectedId) ?? null, [entries, selectedId]);

    useEffect(() => {
        if (!shown || !stickToBottom.current) return;
        const node = listRef.current;
        if (node) node.scrollTop = node.scrollHeight;
    }, [filtered.length, shown]);

    const onListScroll = () => {
        const node = listRef.current;
        if (!node) return;
        stickToBottom.current = node.scrollHeight - node.scrollTop - node.clientHeight < 24;
    };

    const togglePause = () => {
        const next = !pausedState;
        setPausedState(next);
        setPaused(next);
    };

    const persistHeight = useCallback((value) => {
        try {
            globalThis.localStorage?.setItem?.(HEIGHT_KEY, String(value));
        } catch {
            // ignore (quota, privacy mode)
        }
    }, []);

    const clampHeight = useCallback((value) => {
        const maxHeight = Math.max(MIN_HEIGHT, (globalThis.innerHeight ?? 800) - 80);
        return Math.min(maxHeight, Math.max(MIN_HEIGHT, value));
    }, []);

    // Drag the top edge to resize; the height is remembered across sessions.
    const startResize = useCallback((e) => {
        if (e.button !== 0) return;
        e.preventDefault();
        const startY = e.clientY;
        const startHeight = e.currentTarget.closest(".netdiag-panel")?.offsetHeight ?? DEFAULT_HEIGHT;
        let latest = startHeight;

        const onMove = (event) => {
            latest = clampHeight(startHeight + (startY - event.clientY));
            setHeight(latest);
        };
        const onUp = () => {
            document.removeEventListener("pointermove", onMove);
            document.removeEventListener("pointerup", onUp);
            persistHeight(latest);
        };
        document.addEventListener("pointermove", onMove);
        document.addEventListener("pointerup", onUp);
    }, [clampHeight, persistHeight]);

    // The handle is a button so it can also be resized from the keyboard — as a bare div with a
    // separator role it announced an affordance that only pointer users could actually operate.
    const onResizeKeyDown = useCallback((e) => {
        const step = e.shiftKey ? 40 : 8;
        let delta = 0;
        if (e.key === "ArrowUp") delta = step;
        else if (e.key === "ArrowDown") delta = -step;
        else return;

        e.preventDefault();
        const next = clampHeight(height + delta);
        setHeight(next);
        persistHeight(next);
    }, [height, clampHeight, persistHeight]);

    if (!enabled) return null;

    // "12 requests" while unfiltered, "3 of 12 requests" once a filter narrows the list.
    const requestNoun = entries.length === 1 ? "request" : "requests";
    const countLabel = filtered.length === entries.length
        ? `${entries.length} ${requestNoun}`
        : `${filtered.length} of ${entries.length} requests`;

    return (<>
        {!open && (
            <button
                type="button"
                className="netdiag-handle"
                onClick={() => setOpen(true)}
                data-testid="netdiag-handle"
                title="Open network diagnostics"
            >
                <FiActivity />
                Network
                {entries.length > 0 && <span className="netdiag-handle-count">{entries.length}</span>}
            </button>
        )}

        {mounted && (
            <div
                className={`netdiag-panel${shown ? " netdiag-open" : ""}`}
                style={{ height: `${height}px` }}
                data-testid="netdiag-panel"
            >
                <button
                    type="button"
                    className="netdiag-resizer"
                    onPointerDown={startResize}
                    onKeyDown={onResizeKeyDown}
                    aria-label="Resize network diagnostics panel"
                />

                <div className="netdiag-toolbar">
                    <span className="netdiag-title">
                        <FiActivity className="me-1" />
                        Network Diagnostics
                    </span>

                    <button type="button" className="netdiag-btn" onClick={togglePause} data-testid="netdiag-pause">
                        {pausedState ? <><FiPlay /> Resume</> : <><FiPause /> Pause</>}
                    </button>
                    <button type="button" className="netdiag-btn" onClick={clearEntries} data-testid="netdiag-clear">
                        <FiTrash2 /> Clear
                    </button>

                    <input
                        type="text"
                        className="netdiag-filter"
                        value={filter}
                        onChange={(e) => setFilter(e.target.value)}
                        placeholder="Filter by URL, method or status…"
                        aria-label="Filter requests"
                    />
                    <label className="netdiag-check">
                        <input type="checkbox" checked={errorsOnly} onChange={(e) => setErrorsOnly(e.target.checked)} />
                        <span>Errors only</span>
                    </label>

                    <span className="netdiag-count" title={`The most recent ${MAX_ENTRIES} requests are kept`}>
                        {countLabel}
                        {pausedState ? " · paused" : ""}
                    </span>

                    <button
                        type="button"
                        className="netdiag-btn netdiag-close"
                        onClick={() => setOpen(false)}
                        aria-label="Close network diagnostics"
                    >
                        <FiX />
                    </button>
                </div>

                <div className="netdiag-body">
                    <div className="netdiag-list-pane">
                        <div className="netdiag-row netdiag-row-head">
                            <span>Method</span>
                            <span>Status</span>
                            <span>Endpoint</span>
                            <span>Type</span>
                            <span>Size</span>
                            <span>Time</span>
                            <span>Started</span>
                        </div>
                        <div className="netdiag-list" ref={listRef} onScroll={onListScroll}>
                            {filtered.length === 0 && (
                                <div className="netdiag-empty-note netdiag-list-empty">
                                    {entries.length === 0
                                        ? "No requests captured yet. Traffic appears here as the app makes it."
                                        : "No requests match the current filter."}
                                </div>
                            )}
                            {filtered.map((entry) => {
                                const { host, path } = splitUrl(entry.url);
                                return (
                                    <button
                                        type="button"
                                        key={entry.id}
                                        className={`netdiag-row netdiag-row-item${entry.id === selectedId ? " netdiag-row-selected" : ""}`}
                                        onClick={() => setSelectedId(entry.id)}
                                    >
                                        <span className="netdiag-method">{entry.method}</span>
                                        <span className={`netdiag-status ${statusClass(entry)}`}>{statusLabel(entry)}</span>
                                        <span className="netdiag-endpoint">
                                            <span className="netdiag-path">{path}</span>
                                            <span className="netdiag-host">{host}</span>
                                        </span>
                                        <span className="netdiag-muted">{shortContentType(entry.contentType)}</span>
                                        <span className="netdiag-muted">{formatBytes(entry.size)}</span>
                                        <span className="netdiag-muted">{formatDuration(entry.durationMs)}</span>
                                        <span className="netdiag-muted">{formatClock(entry.startedAt)}</span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                    <NetworkRequestDetail entry={selected} />
                </div>
            </div>
        )}
    </>);
};

export default NetworkDiagnostics;
